import { isDamageEntry, type CheckHistoryEntry } from '@dcc/rules/rules/rollHistory.ts';
import { useState } from 'react';
import type { Character } from '@dcc/rules/domain/character.ts';
import type { SessionState } from '@dcc/rules/domain/session.ts';
import type { EffectiveCharacter } from '@dcc/rules/app/effectiveCharacter.ts';
import { UNSKILLED_CHECK } from '@dcc/rules/app/rollService.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import type { Skill } from '@dcc/rules/content/types.ts';
import { canRollSkill } from '@dcc/rules/rules/skillAvailability.ts';
import { STAT_IDS, STAT_LABELS, type StatId } from '@dcc/rules/domain/stats.ts';

export function untrainedChoices(effective: EffectiveCharacter): Skill[] {
  return contentRegistry.allSkills().filter(skill => {
    const available = canRollSkill(skill, effective.effectiveSkillRank(skill.id));
    return skill.kind !== 'spell' && available.rollable && available.untrained;
  }).sort((a, b) => a.name.localeCompare(b.name));
}

export function UnskilledPanel({ character, effective, session, disabled, onRoll }: {
  character: Character; effective: EffectiveCharacter; session: SessionState; disabled: boolean;
  onRoll: (skill: Skill, stat: StatId) => void;
}) {
  const [stat, setStat] = useState<StatId | null>(null);
  const [skillId, setSkillId] = useState('');
  const choices = untrainedChoices(effective);
  const named = choices.find(s => s.id === skillId);
  const valid = skillId === '' || named !== undefined;
  const tutorial = character.floor <= 2;
  const recent = session.rollHistory.filter((entry): entry is CheckHistoryEntry => !isDamageEntry(entry) && entry.untrained).slice(0, 10);
  return <section className="dcc-creator-card" aria-label="Unskilled checks">
    <h2>Unskilled</h2>
    <p className="dcc-creator-help">Always Disadvantage: roll 2d20, keep the lower die, add the chosen Stat modifier. No Skill Rank.</p>
    <div className="dcc-inline-stat-choices">{STAT_IDS.map(id => <button type="button" key={id}
      disabled={disabled} aria-pressed={stat === id} className={stat === id ? 'dcc-mini-mode is-selected' : 'dcc-mini-mode'}
      onClick={() => setStat(id)}>{STAT_LABELS[id].full}</button>)}</div>
    <label className="dcc-creator-field">Skill attempted (optional)
      <select value={skillId} disabled={disabled} onChange={e => setSkillId(e.target.value)}>
        <option value="">Generic Unskilled check</option>
        {!valid && <option value={skillId}>Previously selected Skill is now trained or unavailable</option>}
        {choices.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
    </label>
    <p className="dcc-creator-help">{tutorial ? 'Choose the actual Skill to record it for the Tutorial-floor learning checkpoint. Generic checks are logged but cannot award an unnamed Skill.' : 'Attempts are logged. Floor 3 and later do not grant new Skills through untrained attempts.'}</p>
    <button type="button" disabled={disabled || stat === null || !valid} onClick={() => stat && onRoll(named ?? UNSKILLED_CHECK, stat)}>Roll Unskilled · Disadvantage</button>
    {tutorial && <p className="dcc-creator-help">Learning candidates this interval: {session.untrainedSkillsAttempted.map(id => contentRegistry.getSkill(id)?.name ?? id).join(', ') || 'None yet'}</p>}
    {recent.length > 0 && <details><summary>Recent untrained attempts</summary><ul>{recent.map(entry => <li key={entry.id}>{entry.label}: {entry.total} · {new Date(entry.timestamp).toLocaleString()}</li>)}</ul></details>}
  </section>;
}
